/**
 * Enhanced Authentication Logger for Grafana Loki
 * Provides structured logging for authentication events with Grafana integration
 */

import { logger } from './Logger';

export interface AuthLogData {
  email: string;
  displayName?: string;
  userAgent?: string;
  ipAddress?: string;
  timestamp: string;
  sessionId?: string;
  action: 'login_attempt' | 'login_success' | 'login_failed' | 'authorization_check' | 'authorization_granted' | 'authorization_denied' | 'logout';
  isAuthorized?: boolean;
  error?: string;
  metadata?: Record<string, any>;
}

class AuthLogger {
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private static getUserAgent(): string {
    return navigator.userAgent || 'Unknown';
  }

  private static getClientIP(): string {
    // In production, this would be set by your reverse proxy/load balancer
    return 'client_ip_not_available';
  }

  // Enhanced logging that uses the new unified logger
  private static logAuthEvent(level: 'info' | 'warn' | 'error', data: AuthLogData): void {
    // Use the unified logger for consistent formatting
    logger.logAuth({
      userEmail: data.email,
      displayName: data.displayName,
      sessionId: data.sessionId,
      authAction: data.action,
      isAuthorized: data.isAuthorized,
      errorMessage: data.error,
      metadata: {
        ...data.metadata,
        userAgent: data.userAgent || this.getUserAgent(),
        ipAddress: data.ipAddress || this.getClientIP(),
      }
    });

    // Development-specific debug logging
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`Auth Event: ${data.action}`, {
        email: data.email,
        authorized: data.isAuthorized,
        error: data.error,
        metadata: data.metadata
      });
    }
  }

  static logLoginAttempt(email: string, displayName?: string): void {
    this.logAuthEvent('info', {
      email,
      displayName,
      timestamp: new Date().toISOString(),
      action: 'login_attempt',
      metadata: {
        description: 'User initiated login process',
        loginMethod: 'microsoft_azure_ad'
      },
    });
  }

  static logLoginSuccess(email: string, displayName?: string, sessionId?: string): void {
    this.logAuthEvent('info', {
      email,
      displayName,
      timestamp: new Date().toISOString(),
      action: 'login_success',
      sessionId,
      metadata: {
        description: 'User successfully authenticated with Azure AD',
        loginMethod: 'microsoft_azure_ad'
      },
    });
  }

  static logLoginFailed(email: string, error: string): void {
    this.logAuthEvent('error', {
      email,
      timestamp: new Date().toISOString(),
      action: 'login_failed',
      error,
      metadata: {
        description: 'Authentication failed',
        loginMethod: 'microsoft_azure_ad'
      },
    });
  }

  static logAuthorizationCheck(email: string, displayName?: string): void {
    this.logAuthEvent('info', {
      email,
      displayName,
      timestamp: new Date().toISOString(),
      action: 'authorization_check',
      metadata: {
        description: 'Checking user authorization against whitelist/groups',
      },
    });
  }

  static logAuthorizationGranted(email: string, displayName?: string, sessionId?: string, authMethod?: 'group' | 'email' | 'database'): void {
    this.logAuthEvent('info', {
      email,
      displayName,
      timestamp: new Date().toISOString(),
      action: 'authorization_granted',
      isAuthorized: true,
      sessionId,
      metadata: {
        description: 'User authorized - access granted to ShipmentCoCaptain',
        authorizationMethod: authMethod || 'email',
        whitelistMatch: authMethod === 'email',
        groupMatch: authMethod === 'group',
        databaseMatch: authMethod === 'database',
      },
    });
  }

  static logAuthorizationDenied(email: string, displayName?: string, reason?: string): void {
    this.logAuthEvent('warn', {
      email,
      displayName,
      timestamp: new Date().toISOString(),
      action: 'authorization_denied',
      isAuthorized: false,
      metadata: {
        description: 'User not authorized - access denied',
        reason: reason || 'Email not in whitelist/group',
        whitelistMatch: false,
        securityEvent: true,
        denialReason: reason
      },
    });
  }

  static logLogout(email: string, sessionId?: string): void {
    this.logAuthEvent('info', {
      email,
      timestamp: new Date().toISOString(),
      action: 'logout',
      sessionId,
      metadata: {
        description: 'User logged out',
      },
    });
  }

  static logError(email: string, error: string, action: string): void {
    this.logAuthEvent('error', {
      email,
      timestamp: new Date().toISOString(),
      action: action as any,
      error,
      metadata: {
        description: 'Authentication system error',
        errorContext: action
      },
    });
  }

  // New method to log unauthorized access attempts with more detail
  static logUnauthorizedAccess(email: string, requestedPath?: string, userGroups?: string[], requiredGroups?: string[]): void {
    this.logAuthEvent('warn', {
      email,
      timestamp: new Date().toISOString(),
      action: 'authorization_denied',
      isAuthorized: false,
      metadata: {
        description: 'Unauthorized access attempt',
        requestedPath,
        userGroups: userGroups?.length || 0,
        requiredGroups: requiredGroups?.length || 0,
        securityEvent: true,
        unauthorizedAttempt: true
      },
    });
  }

  // Method to log session events
  static logSessionEvent(email: string, event: 'session_start' | 'session_extend' | 'session_expire', sessionId?: string, duration?: number): void {
    this.logAuthEvent('info', {
      email,
      timestamp: new Date().toISOString(),
      action: event as any,
      sessionId,
      metadata: {
        description: `Session ${event.replace('_', ' ')}`,
        sessionDuration: duration
      },
    });
  }
}

export default AuthLogger;
